import { useQuery } from "@tanstack/react-query";
import { can, type Me, type Permission, type RanchDTO } from "../../contract";
import { api, ApiError } from "./api";
import { setLanguage } from "./i18n";
import { setSnapshotOwner } from "./offline";

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ["/me"],
    queryFn: async () => {
      try {
        const me = await api.get<Me>("/me");
        if (me.person.locale) setLanguage(me.person.locale);
        // Saved copies for offline reading belong to this ranch and person only.
        setSnapshotOwner(me.ranch && me.membership ? `${me.ranch.id}:${me.membership.id}` : "");
        return me;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/** The signed-in ranch. Only use inside the signed-in shell. */
export function useRanch(): RanchDTO {
  const { data } = useMe();
  if (!data?.ranch) throw new Error("useRanch outside a ranch");
  return data.ranch;
}

export function useCan(): (p: Permission) => boolean {
  const { data } = useMe();
  return (p) => can(data?.membership?.role, p);
}
